// Simple client-side router for the cat feeder app
class Router {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;
    this.isHandlingRoute = false;
    this.currentRoomId = null;
    this.init();
  }

  init() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Listen for navigation events
    window.addEventListener('popstate', (e) => {
      this.handleRoute();
    });
  }

  runInitialRoute() {
    this.handleRoute();
  }

  // Add a route
  addRoute(path, handler) {
    this.routes.set(path, handler);
  }

  // Navigate to a route
  navigate(path, data = null) {
    // Prevent infinite loops
    if (this.isHandlingRoute) {
      return;
    }
    
    // Update URL without page reload
    history.pushState(data, '', path);
    this.handleRoute();
  }

  // Handle the current route
  handleRoute(path = window.location.pathname) {
    if (this.isHandlingRoute) {
      return;
    }
    
    this.isHandlingRoute = true;
    
    // Check if we're leaving a livestream room
    const livestreamPattern = /^\/(?:rooms\/)?[^/]+$/;
    const isLeavingLivestream = this.currentRoomId && !livestreamPattern.test(path);
    if (isLeavingLivestream) {
      this.leaveCurrentRoom();
    }
    
    // Try exact match first
    if (this.routes.has(path)) {
      this.routes.get(path)();
      this.isHandlingRoute = false;
      return;
    }

    // Try pattern matching for dynamic routes
    for (const [routePath, handler] of this.routes) {
      if (this.matchRoute(routePath, path)) {
        handler();
        this.isHandlingRoute = false;
        return;
      }
    }

    this.isHandlingRoute = false;
    this.handleNotFound();
  }

  // Simple route matching for dynamic routes like /rooms/:slug
  matchRoute(routePattern, actualPath) {
    const routeParts = routePattern.split('/');
    const pathParts = actualPath.split('/');

    if (routeParts.length !== pathParts.length) {
      return false;
    }

    for (let i = 0; i < routeParts.length; i++) {
      if (routeParts[i].startsWith(':')) {
        // Dynamic parameter - store it
        const paramName = routeParts[i].substring(1);
        window.routeParams = window.routeParams || {};
        window.routeParams[paramName] = pathParts[i];
        continue;
      }
      
      if (routeParts[i] !== pathParts[i]) {
        return false;
      }
    }

    return true;
  }

  // Handle 404 or redirect to home
  handleNotFound() {
    // Only redirect if we're not already on the home page
    if (window.location.pathname !== '/') {
      this.navigate('/');
    } else {
      // If we're already on home and no route is registered, just show a message
      console.warn('No route handler found for:', window.location.pathname);
    }
  }

  // Get current route parameters
  getParams() {
    return window.routeParams || {};
  }

  // Clear route parameters
  clearParams() {
    window.routeParams = {};
  }

  // Set current room ID for socket management
  setCurrentRoom(roomId) {
    this.currentRoomId = roomId;
  }

  // Get current room ID
  getCurrentRoom() {
    return this.currentRoomId;
  }

  // Clear current room (call when leaving)
  clearCurrentRoom() {
    this.currentRoomId = null;
  }

  // Leave current room and emit socket event
  leaveCurrentRoom() {
    if (!this.currentRoomId) return;
    const roomId = this.currentRoomId; // capture before async work
    const cleanupPromises = [];

    cleanupPromises.push(
      import('./socket.js')
        .then(({ socket }) => {
          socket.emit('leave_room', { roomId: roomId });
        })
        .catch(() => {})
    );

    cleanupPromises.push(
      import('./livestream-components.js')
        .then(({ teardownLivestreamComponents }) => {
          if (typeof teardownLivestreamComponents === 'function') {
            teardownLivestreamComponents();
          }
        })
        .catch(() => {})
    );

    Promise.all(cleanupPromises).finally(() => {
      this.clearCurrentRoom();
    });
  }
}

// Create global router instance
window.router = new Router();

export { Router };
export const router = window.router;
